import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { CAT } from "@/constants/testIds";

const Row = ({ c, onSaved, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(c.name);
  const save = async () => {
    try {
      await api.put(`/categories/${c.id}`, { name });
      toast.success("Kategori diperbarui");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <li className="flex items-center gap-3 px-5 py-4" data-testid={CAT.row}>
      {editing ? (
        <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 max-w-sm" autoFocus data-testid={CAT.editInput} />
      ) : (
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 truncate">{c.name}</p>
          <p className="text-xs text-slate-500">{c.template_count} template</p>
        </div>
      )}
      <div className="ml-auto flex gap-1">
        {editing ? (
          <>
            <button onClick={save} className="rounded-md p-2 text-emerald-600 hover:bg-emerald-50" aria-label="Simpan" data-testid={CAT.saveButton}><Check className="h-4 w-4" /></button>
            <button onClick={() => { setEditing(false); setName(c.name); }} className="rounded-md p-2 text-slate-500 hover:bg-slate-100" aria-label="Batal"><X className="h-4 w-4" /></button>
          </>
        ) : (
          <>
            <button onClick={() => setEditing(true)} className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900" aria-label="Edit" data-testid={CAT.editButton}><Pencil className="h-4 w-4" /></button>
            <button onClick={() => onDelete(c)} className="rounded-md p-2 text-slate-500 hover:bg-red-50 hover:text-red-600" aria-label="Hapus" data-testid={CAT.deleteButton}><Trash2 className="h-4 w-4" /></button>
          </>
        )}
      </div>
    </li>
  );
};

export default function Categories() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/categories");
    setItems(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/categories", { name });
      setName("");
      toast.success("Kategori ditambahkan");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/categories/${toDelete.id}`);
      toast.success("Kategori dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setToDelete(null);
  };

  return (
    <div className="max-w-3xl space-y-8">
      <div className="fade-up">
        <p className="overline-label">Pengaturan</p>
        <h1 className="font-heading mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Kelola Kategori</h1>
        <p className="mt-2 text-sm text-slate-500">Kategori membantu CS menyaring template dengan cepat.</p>
      </div>
      <form onSubmit={add} className="flex gap-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nama kategori baru, contoh: Pembayaran" required className="h-11 bg-white" data-testid={CAT.nameInput} />
        <Button type="submit" className="h-11 rounded-full bg-brand hover:bg-brand-dark text-white px-6" data-testid={CAT.addButton}><Plus className="h-4 w-4 mr-1" /> Tambah</Button>
      </form>
      <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
        {items.length === 0 && <li className="px-5 py-10 text-center text-sm text-slate-500">Belum ada kategori.</li>}
        {items.map((c) => <Row key={c.id} c={c} onSaved={load} onDelete={setToDelete} />)}
      </ul>
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} onConfirm={remove}
        title="Hapus kategori?" description={`Kategori "${toDelete?.name}" akan dihapus. Template di dalamnya tidak ikut terhapus dan menjadi "Tanpa kategori".`} />
    </div>
  );
}
